import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { BedIncludeResMock, BedResMock } from '../types/bed.type'
import { GeneralResMock } from '../types/general.type'

type Props = {
  page: number
  limit: number
  query: string
  sortDir: 'asc' | 'desc'
  id: string
  status: string
  bed_type: string
  room_id: string
  assignStatus: string
  facility_id: string
}
export const useBed = (arg: Partial<Props>) => {
  const page = arg?.page || 1
  const limit = arg?.limit || 10
  const query = arg?.query
  const sortDir = arg?.sortDir || 'asc'
  const id = arg?.id
  const status = arg?.status
  const bed_type = arg?.bed_type
  const room_id = arg?.room_id
  const assignStatus = arg?.assignStatus
  const facility_id = arg?.facility_id

  const { data, isLoading: bedsIsLoading } = useQuery({
    enabled: !!room_id,
    queryFn: async () => {
      let endpoint = `/beds?sort_dir=${sortDir}&room_id=${room_id}`
      if (facility_id) endpoint += `&facility_id=${facility_id}`
      if (page) endpoint = `${endpoint}&page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}`
      if (query) endpoint = `${endpoint}&query=${query}`
      if (status && status !== 'All') endpoint += `&status=${status}&`
      if (bed_type) endpoint = `${endpoint}&bed_type=${bed_type}`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<BedResMock[], BedIncludeResMock[]>>(endpoint)
      return data
    },
    queryKey: ['beds', { limit, page, query, room_id, sortDir, status, facility_id }]
  })

  const { data: unQueryBeds, isLoading: unQueryIsLoading } = useQuery({
    queryFn: async () => {
      let endpoint = `/beds?sort_dir=${sortDir}`
      if (assignStatus && assignStatus !== 'all') endpoint += `&status=${assignStatus}&`
      if (query) endpoint = `${endpoint}&query=${query}`
      if (facility_id) endpoint += `&facility_id=${facility_id}`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<BedResMock[], BedIncludeResMock[]>>(endpoint)
      return data
    },
    queryKey: ['beds', assignStatus, facility_id]
  })

  const { data: singleBed } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<BedResMock, BedIncludeResMock[]>>(`/beds/${id}`)
      return data
    },
    queryKey: ['beds', id]
  })

  const { mutateAsync: createBed } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`beds`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] })
    }
  })

  const { mutateAsync: updateBed } = useMutation({
    mutationFn: async ({ data, id }: { data: any; id: string }) => {
      const { data: updatedData } = await AXIOS_INSTANCE.patch(`beds/${id}`, data, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return updatedData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] })
    }
  })

  const { mutateAsync: deleteBed } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.delete(`beds/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] })
    }
  })

  return {
    beds: data?.data,
    bedsIsLoading,
    createBed,
    deleteBed,
    includedData: data?.included,
    pagination: data?.meta.pagination,
    singleBed,
    unQueryBeds: unQueryBeds?.data,
    unQueryIncData: unQueryBeds?.included,
    unQueryIsLoading,
    updateBed
  }
}
